import { useCallback, useEffect, useState } from "react";
import { Button, Spinner } from "@telegram-apps/telegram-ui";

import type { TelegramAuthResult } from "../types";

let scriptPromise: Promise<void> | null = null;

/** Идемпотентно подгружает SDK telegram-login.js и резолвится, когда готов. */
function loadTelegramLogin(src: string): Promise<void> {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.Telegram?.Login) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error("Не удалось загрузить SDK Telegram"));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
}

type Props = {
  clientId: string;
  scriptUrl: string;
  onAuth: (idToken: string) => void;
  onError: (message: string) => void;
};

/**
 * Кнопка входа через Telegram (OpenID Connect). По клику SDK открывает popup на
 * oauth.telegram.org и возвращает подписанный id_token, который проверяет бэкенд.
 * Работает только на origin, добавленном в BotFather → Bot Settings → Web Login.
 */
export function TelegramLoginButton({
  clientId,
  scriptUrl,
  onAuth,
  onError,
}: Props) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;
    loadTelegramLogin(scriptUrl)
      .then(() => {
        if (active) setReady(true);
      })
      .catch((e: unknown) => {
        if (active) onError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      active = false;
    };
  }, [scriptUrl, onError]);

  const onClick = useCallback(() => {
    const login = window.Telegram?.Login;
    if (!login) {
      onError("SDK Telegram ещё не готов");
      return;
    }
    login.auth({ client_id: Number(clientId) }, (data: TelegramAuthResult) => {
      if (!data || data.error) {
        onError(data?.error || "Вход отменён");
        return;
      }
      if (data.id_token) onAuth(data.id_token);
      else onError("Не получен id_token");
    });
  }, [clientId, onAuth, onError]);

  if (!ready) return <Spinner size="m" />;

  return (
    <Button size="l" stretched onClick={onClick}>
      Войти через Telegram
    </Button>
  );
}
